import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import WidgetLoading from '../../../components/widgetLoading';
import {
  AV_PROFILE_OPEN_EVENT,
  type ProfileOpenDetail
} from '../../askVantageV2/components/AskVantageResponse';
import { useRelativeTime } from '../hooks/useRelativeTime';
import type { Signal, SignalSnapshotCell, SignalStatus, SignalTrend, SignalTunable } from '../types';
import { SignalInfoTooltip } from './SignalInfoTooltip';
import { SIGNALS_FONT_FAMILY } from '../theme/constants';

interface SignalCardProps {
  signal: Signal;
  onHiddenChange?: (signal: Signal, hidden: boolean) => void;
  onTunableChange?: (signal: Signal, tunableValues: Record<string, number | boolean>) => Promise<void>;
  onNotificationChange?: (signal: Signal, enabled: boolean) => Promise<void>;
  onSettingsOpen?: (signal: Signal) => Promise<SignalTunable[]>;
  onExpand?: (signal: Signal) => void;
  isPreferenceSaving?: boolean;
}

// Map the integer color enum from the API (`Signals::Enums::COLOR`) to a
// status keyword. Mirrors the Ruby side: 0 neutral / 1 green / 2 amber /
// 3 red. Used for tinting individual data-box cells.
const cellStatus = (c?: number): SignalStatus | 'neutral' => {
  switch (c) {
    case 1:
      return 'green';
    case 2:
      return 'amber';
    case 3:
      return 'red';
    default:
      return 'neutral';
  }
};

const trendIcon = (trend: SignalTrend): string => {
  if (trend === 'worsening') return '↘';
  if (trend === 'improving') return '↗';
  return '→';
};

const trendLabel = (trend: SignalTrend): string => {
  if (trend === 'worsening') return 'Worsening';
  if (trend === 'improving') return 'Improving';
  return 'Flat';
};

const unconfiguredNarrative = (signal: Signal): string => {
  const snapshotNarrative = signal.data_snapshot?.find((cell) => cell.v)?.v;
  if (snapshotNarrative) return snapshotNarrative;

  if (signal.requires_kpi_target) {
    return 'This signal is waiting on a KPI target. Configure it to start tracking.';
  }

  return 'This signal is building its historical baseline. It will start tracking after enough comparable order history is available.';
};

const ctaLabel = (cell?: { cta_label?: string; ctaLabel?: string }): string | undefined =>
  cell?.cta_label || cell?.ctaLabel;

const ctaUrl = (cell?: { cta_url?: string; ctaUrl?: string }): string | undefined =>
  cell?.cta_url || cell?.ctaUrl;

const ctaProfile = (cell?: SignalSnapshotCell): ProfileOpenDetail | undefined =>
  cell?.cta_profile || cell?.ctaProfile;

/** Long location-style values read better stacked than squeezed beside the label. */
const isStackedSnapshotRow = (cell: SignalSnapshotCell): boolean => {
  const value = (cell.v || '').trim();
  if (!value) return false;
  if (cell.l === 'Biggest drop') return true;
  return value.length > 26;
};

const openProfileCta = (detail: ProfileOpenDetail) => {
  window.dispatchEvent(
    new CustomEvent(AV_PROFILE_OPEN_EVENT, {
      detail
    })
  );
};

const numericStep = (tunable: SignalTunable): number => {
  return typeof tunable.step === 'number' && tunable.step > 0 ? tunable.step : 1;
};

const clamp = (value: number, tunable: SignalTunable): number => {
  let next = value;
  if (typeof tunable.min === 'number') next = Math.max(next, tunable.min);
  if (typeof tunable.max === 'number') next = Math.min(next, tunable.max);
  return next;
};

const snapToStep = (value: number, tunable: SignalTunable): number => {
  const step = numericStep(tunable);
  const snapped = Math.round(value / step) * step;
  const precision = String(step).split('.')[1]?.length ?? 0;
  return Number(clamp(snapped, tunable).toFixed(Math.max(precision, 0)));
};

const normalizeThresholdPairs = (
  tunables: SignalTunable[],
  values: Record<string, number | boolean>,
  changedKey?: string
): Record<string, number | boolean> => {
  const next = { ...values };
  const thresholdTunables = tunables.filter((tunable) => tunable.threshold && tunable.direction);
  const directions = Array.from(new Set(thresholdTunables.map((tunable) => tunable.direction)));

  directions.forEach((direction) => {
    const red = thresholdTunables.find((tunable) => tunable.direction === direction && tunable.severity === 'red');
    const amber = thresholdTunables.find((tunable) => tunable.direction === direction && tunable.severity === 'amber');
    if (!red || !amber) return;

    const redValue = Number(next[red.key] ?? red.value);
    const amberValue = Number(next[amber.key] ?? amber.value);
    if (!Number.isFinite(redValue) || !Number.isFinite(amberValue)) return;

    const step = Math.max(numericStep(red), numericStep(amber));
    if (direction === 'below' && redValue >= amberValue) {
      if (changedKey === amber.key) {
        next[red.key] = snapToStep(amberValue - step, red);
      } else {
        next[amber.key] = snapToStep(redValue + step, amber);
        if (Number(next[amber.key]) <= redValue) next[red.key] = snapToStep(Number(next[amber.key]) - step, red);
      }
    }

    if (direction === 'above' && redValue <= amberValue) {
      if (changedKey === amber.key) {
        next[red.key] = snapToStep(amberValue + step, red);
      } else {
        next[amber.key] = snapToStep(redValue - step, amber);
        if (Number(next[amber.key]) >= redValue) next[red.key] = snapToStep(Number(next[amber.key]) + step, red);
      }
    }
  });

  return next;
};

export const SignalCard = ({
  signal,
  onHiddenChange,
  onTunableChange,
  onNotificationChange,
  onSettingsOpen,
  onExpand,
  isPreferenceSaving
}: SignalCardProps) => {
  const relativeTime = useRelativeTime(signal.evaluated_at);
  const isEvaluating = signal.status === 'evaluating';
  const isUnconfigured = signal.status === 'unconfigured';
  const showEvaluatingLoader = isEvaluating && !signal.is_hidden;
  const showHiddenPaused =
    signal.is_hidden && !showEvaluatingLoader && signal.data_snapshot.length === 0;
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasTuningChanges, setHasTuningChanges] = useState(false);
  const [isTuningLoading, setIsTuningLoading] = useState(false);
  const tunables = signal.tunables || [];
  const ctaCell = signal.data_snapshot?.find(
    (cell) => ctaLabel(cell) && (ctaUrl(cell) || ctaProfile(cell))
  );
  const hasDetails = Boolean(signal.details?.rows?.length);
  const [tunableValues, setTunableValues] = useState<Record<string, number | boolean>>(() => {
    return tunables.reduce<Record<string, number | boolean>>((acc, tunable) => {
      acc[tunable.key] = tunable.value;
      return acc;
    }, {});
  });
  // Action text is only meaningful when there's something to act on.
  // Green / evaluating / unconfigured cards skip the action block; the
  // bottom-anchor margin on ActionBlock / Footer (see styled components
  // below) keeps cards in the same grid row aligned at the footer line.
  const showAction =
    !showEvaluatingLoader &&
    !isUnconfigured &&
    !showHiddenPaused &&
    (signal.status === 'red' || signal.status === 'amber') &&
    Boolean(signal.action_text);

  const handleDone = async () => {
    if (hasTuningChanges && onTunableChange) {
      await onTunableChange(signal, normalizeThresholdPairs(tunables, tunableValues));
      setHasTuningChanges(false);
    }
    setIsFlipped(false);
  };

  const handleSettingsOpen = async () => {
    setIsFlipped(true);
    if (!onSettingsOpen) return;

    setIsTuningLoading(true);
    try {
      const freshTunables = await onSettingsOpen(signal);
      setTunableValues(
        normalizeThresholdPairs(
          freshTunables,
          freshTunables.reduce<Record<string, number | boolean>>((acc, tunable) => {
            acc[tunable.key] = tunable.value;
            return acc;
          }, {})
        )
      );
      setHasTuningChanges(false);
    } finally {
      setIsTuningLoading(false);
    }
  };

  return (
    <CardScene>
      <CardInner $flipped={isFlipped}>
        <CardFace $status={signal.status}>
          <Header>
            <HeaderLeft>
              <Eyebrow>{signal.eyebrow}</Eyebrow>
              <Title>{signal.title}</Title>
            </HeaderLeft>
            <HeaderActions>
              <SignalInfoTooltip
                label="What this means"
                content={signal.description || signal.action_text || ''}
              >
                <IconButton type="button" aria-label={`About ${signal.title}`}>
                  i
                </IconButton>
              </SignalInfoTooltip>
              <Badge $status={signal.status}>
                <BadgeDot $status={signal.status} />
                {signal.badge_label}
              </Badge>
            </HeaderActions>
          </Header>

          <KpiLabel>{signal.kpi}</KpiLabel>

          {showEvaluatingLoader ? (
            <LoadingState>
              <WidgetLoading compact />
            </LoadingState>
          ) : showHiddenPaused ? (
            <Narrative>
              This signal is hidden. Restore it to resume live tracking and refresh its data.
            </Narrative>
          ) : isUnconfigured && ctaCell ? (
            <CtaBlock>
              <Narrative>{ctaCell.v}</Narrative>
              {ctaProfile(ctaCell) ? (
                <CtaButton type="button" onClick={() => openProfileCta(ctaProfile(ctaCell)!)}>
                  {ctaLabel(ctaCell)}
                </CtaButton>
              ) : (
                <CtaLink href={ctaUrl(ctaCell)} target="_blank" rel="noopener noreferrer">
                  {ctaLabel(ctaCell)}
                </CtaLink>
              )}
            </CtaBlock>
          ) : isUnconfigured ? (
            <Narrative>{unconfiguredNarrative(signal)}</Narrative>
          ) : (
            <EvidenceBlock>
              <DataBox $status={signal.status}>
                {signal.data_snapshot.map((cell, idx) => (
                  <DataRow
                    key={idx}
                    $status={cellStatus(cell.c)}
                    $stacked={isStackedSnapshotRow(cell)}
                    title={isStackedSnapshotRow(cell) ? cell.v : undefined}
                  >
                    <span>{cell.l}</span>
                    <strong>{cell.v}</strong>
                  </DataRow>
                ))}
              </DataBox>
              {hasDetails && (
                <DrillDownButton
                  type="button"
                  $status={signal.status}
                  onClick={() => onExpand?.(signal)}
                >
                  Drill down
                </DrillDownButton>
              )}
            </EvidenceBlock>
          )}

          <TrendSlot>
            {!showEvaluatingLoader && !isUnconfigured && !showHiddenPaused && (
              <TrendPill $trend={signal.trend}>
                <span>{trendIcon(signal.trend)}</span>
                {trendLabel(signal.trend)}
              </TrendPill>
            )}
          </TrendSlot>

          <ActionSlot>
            {showAction && (
              <ActionBlock>
                <ActionBadge $status={signal.status}>ACTION</ActionBadge>
                <ActionText>{signal.action_text}</ActionText>
              </ActionBlock>
            )}
          </ActionSlot>

          <Footer>
            <FooterDot $status={signal.status} />
            {relativeTime}
            <FooterButtonGroup>
              <ConfigureButton
                type="button"
                aria-label={`Configure ${signal.title}`}
                onClick={handleSettingsOpen}
              >
                <HiOutlineAdjustmentsHorizontal size={15} aria-hidden />
              </ConfigureButton>
            </FooterButtonGroup>
          </Footer>
        </CardFace>

        <CardBack $status={signal.status}>
          <BackHeader>
            <div>
              <Eyebrow>Signal Settings</Eyebrow>
              <Title>{signal.title}</Title>
            </div>
            <IconButton
              type="button"
              aria-label="Close settings"
              onClick={() => setIsFlipped(false)}
            >
              ×
            </IconButton>
          </BackHeader>

          <BackScroll>
            <BackSection>
              <SectionTitle>Notifications</SectionTitle>
              <ToggleRow>
                <div>
                  <ToggleTitle>Enable Email Notification</ToggleTitle>
                  <ToggleHint>
                    Alerts when this signal escalates to Ready to Act or Act Now. WhatsApp coming soon.
                  </ToggleHint>
                </div>
                <SwitchButton
                  type="button"
                  $on={signal.notifications_enabled}
                  disabled={isPreferenceSaving}
                  onClick={() => onNotificationChange?.(signal, !signal.notifications_enabled)}
                  aria-pressed={signal.notifications_enabled}
                >
                  <span />
                </SwitchButton>
              </ToggleRow>
            </BackSection>

            <BackSection>
              <SectionTitle>Tuning</SectionTitle>
              {isTuningLoading ? (
                <TuningLoading>
                  <WidgetLoading />
                </TuningLoading>
              ) : tunables.length > 0 ? (
                <TunableList>
                  {tunables.map((tunable) => (
                    <TunableRow key={tunable.key} $severity={tunable.threshold ? tunable.severity : undefined}>
                      <TunableLabel>
                        <span>{tunable.label}</span>
                        <strong>
                          {String(tunableValues[tunable.key] ?? tunable.value)}
                          {tunable.unit ? ` ${tunable.unit}` : ''}
                        </strong>
                      </TunableLabel>
                      <input
                        type="range"
                        min={typeof tunable.min === 'number' ? tunable.min : undefined}
                        max={typeof tunable.max === 'number' ? tunable.max : undefined}
                        step={typeof tunable.step === 'number' ? tunable.step : undefined}
                        value={
                          typeof (tunableValues[tunable.key] ?? tunable.value) === 'number'
                            ? Number(tunableValues[tunable.key] ?? tunable.value)
                            : Number(tunable.value)
                        }
                        onChange={(event) => {
                          setTunableValues((current) =>
                            normalizeThresholdPairs(
                              tunables,
                              {
                                ...current,
                                [tunable.key]: Number(event.target.value)
                              },
                              tunable.key
                            )
                          );
                          setHasTuningChanges(true);
                        }}
                      />
                    </TunableRow>
                  ))}
                </TunableList>
              ) : (
                <ToggleHint>No merchant-facing tuning options yet.</ToggleHint>
              )}
            </BackSection>

            <BackSection>
              <SectionTitle>How to read this</SectionTitle>
              <BackCopy>{signal.how_to_read || signal.description || signal.action_text}</BackCopy>
            </BackSection>
          </BackScroll>

          <BackFooter>
            <DangerButton
              type="button"
              disabled={isPreferenceSaving}
              onClick={() => onHiddenChange?.(signal, !signal.is_hidden)}
            >
              {signal.is_hidden ? 'Restore Signal' : 'Hide Signal'}
            </DangerButton>
            <SecondaryButton type="button" disabled={isPreferenceSaving} onClick={handleDone}>
              {hasTuningChanges ? 'Save tuning' : 'Done'}
            </SecondaryButton>
          </BackFooter>
        </CardBack>
      </CardInner>
    </CardScene>
  );
};

// ── Animations ────────────────────────────────────────────────────────────

// Card entry — staggered fade-up handled by the parent's animation-delay.
const fadeUp = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// Quiet ambient breathing: a soft red glow on Act Now cards.
const glowingRed = keyframes`
  0% {
    box-shadow: rgba(252, 67, 67, 0) 0px 0px 0px 0px;
  }
  50% {
    box-shadow: rgba(252, 67, 67, 0.5) 0px 0px 10px 2px;
  }
`;

// ── Styled components ─────────────────────────────────────────────────────

const CardScene = styled.article`
  perspective: 1200px;
  min-height: 470px;
  height: 100%;
`;

const CardInner = styled.div<{ $flipped: boolean }>`
  position: relative;
  min-height: 470px;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ $flipped }) => ($flipped ? 'rotateY(180deg)' : 'rotateY(0deg)')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const tokenForStatus = (theme: any, status: SignalStatus | 'neutral') => {
  const s = theme.signals;
  switch (status) {
    case 'red':
      return { strong: s.red_strong, tint: s.red_tint, text: s.red_text, border: s.red_strong };
    case 'amber':
      return { strong: s.amber_strong, tint: s.amber_tint, text: s.amber_text, border: s.amber_strong };
    case 'green':
      return { strong: s.green_strong, tint: s.green_tint, text: s.green_text, border: s.green_strong };
    default:
      return { strong: s.neutral_strong, tint: s.neutral_tint, text: s.neutral_text, border: s.neutral_strong };
  }
};

const Card = styled.article<{ $status: SignalStatus }>`
  position: relative;
  /* Stretch every card to the tallest sibling in its grid row. Combined
     with the <Spacer> below, this lets positive cards (no action block)
     keep their footer aligned with adjacent action cards instead of
     leaving a ragged grid. */
  height: 100%;
  background: ${({ theme }) => theme.signals.card_bg};
  border: none;
  border-radius: 20px;
  padding: 16px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  filter: drop-shadow(rgba(0, 0, 0, 0.05) 5px 5px 10px);
  font-family: ${SIGNALS_FONT_FAMILY};
  color: ${({ theme }) => theme.signals.text_primary};
  overflow: hidden;
  animation: ${fadeUp} 0.4s ease-out both;
  transition: filter 0.4s ease, background 0.4s ease;

  ${({ $status }) =>
    $status === 'red' &&
    css`
      animation: ${fadeUp} 0.4s ease-out both, ${glowingRed} 4s ease-in-out infinite;
    `}
`;

const CardFace = styled(Card)`
  min-height: 470px;
  backface-visibility: hidden;
`;

const CardBack = styled(CardFace)`
  position: absolute;
  inset: 0;
  transform: rotateY(180deg);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 1px;
`;

const IconButton = styled.button`
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.signals.card_border};
  border-radius: 999px;
  background: ${({ theme }) => theme.signals.chip_bg};
  color: ${({ theme }) => theme.signals.text_secondary};
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.signals.text_primary};
  }
`;

const Eyebrow = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.eyebrow};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.15;
  color: ${({ theme }) => theme.signals.text_primary};
`;

const Badge = styled.span<{ $status: SignalStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ theme, $status }) => tokenForStatus(theme, $status).tint};
  color: ${({ theme, $status }) => tokenForStatus(theme, $status).text};
  transition: background 0.5s ease, color 0.5s ease;
`;

const BadgeDot = styled.span<{ $status: SignalStatus }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme, $status }) => tokenForStatus(theme, $status).strong};
`;

const KpiLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.text_muted};
`;

const LoadingState = styled.div`
  position: relative;
  flex: 1;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Narrative = styled.p`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.signals.text_secondary};
  margin: 0;
`;

const CtaBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const ctaPillStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${({ theme }: { theme: any }) => theme.signals.neutral_strong};
  color: ${({ theme }: { theme: any }) => theme.signals.badge_text};
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CtaLink = styled.a`
  ${ctaPillStyles}
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.signals.badge_text};
    text-decoration: none;
    filter: brightness(0.95);
  }
`;

const CtaButton = styled.button`
  ${ctaPillStyles}
  border: 0;
  cursor: pointer;

  &:hover {
    filter: brightness(0.95);
  }
`;

const DataBox = styled.div<{ $status: SignalStatus }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 78px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme, $status }) => tokenForStatus(theme, $status).tint};
  transition: background 0.5s ease;
`;

const EvidenceBlock = styled.div`
  min-height: 122px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DataRow = styled.div<{ $status: SignalStatus | 'neutral'; $stacked?: boolean }>`
  display: flex;
  gap: ${({ $stacked }) => ($stacked ? '3px' : '10px')};
  font-size: 12px;
  font-weight: 600;

  ${({ $stacked }) =>
    $stacked
      ? css`
          flex-direction: column;
          align-items: stretch;
        `
      : css`
          justify-content: space-between;
          align-items: baseline;
        `}

  span {
    flex-shrink: 0;
    color: ${({ theme }) => theme.signals.text_secondary};
  }

  strong {
    font-weight: 800;
    color: ${({ theme, $status }) =>
      $status === 'neutral'
        ? theme.signals.text_primary
        : tokenForStatus(theme, $status).text};

    ${({ $stacked }) =>
      $stacked
        ? css`
            text-align: left;
            white-space: normal;
            overflow-wrap: anywhere;
            line-height: 1.35;
          `
        : css`
            flex-shrink: 0;
            margin-left: auto;
            text-align: right;
            white-space: nowrap;
          `}
  }
`;

const DrillDownButton = styled.button<{ $status: SignalStatus }>`
  align-self: flex-end;
  min-width: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 9px;
  border: 1px solid ${({ theme, $status }) =>
    $status === 'red' || $status === 'amber'
      ? tokenForStatus(theme, $status).border
      : theme.signals.card_border};
  border-radius: 8px;
  background: ${({ theme }) => theme.signals.chip_bg};
  color: ${({ theme, $status }) =>
    $status === 'red' || $status === 'amber'
      ? tokenForStatus(theme, $status).text
      : theme.signals.text_secondary};
  font-size: 9px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  cursor: pointer;

  &::after {
    content: '→';
    font-size: 14px;
    line-height: 0;
    margin-top: -4px;
  }

  &:hover {
    color: ${({ theme, $status }) =>
      $status === 'red' || $status === 'amber'
        ? tokenForStatus(theme, $status).strong
        : theme.signals.text_primary};
  }
`;

const TrendPill = styled.div<{ $trend: SignalTrend }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme, $trend }) => {
    if ($trend === 'worsening') return theme.signals.red_text;
    if ($trend === 'improving') return theme.signals.green_text;
    return theme.signals.text_muted;
  }};

  span {
    font-size: 14px;
    line-height: 1;
  }
`;

const TrendSlot = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
`;

const ActionSlot = styled.div`
  min-height: 100px;
  display: flex;
  align-items: flex-start;
  margin-top: auto;
  padding-top: 14px;
`;

const ActionBlock = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.signals.divider};
`;

const ActionBadge = styled.span<{ $status: SignalStatus }>`
  position: absolute;
  top: 0;
  left: 0;
  transform: translateY(-50%);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.6px;
  padding: 4px 7px;
  border-radius: 4px;
  background: ${({ theme, $status }) => tokenForStatus(theme, $status).strong};
  color: ${({ theme }) => theme.signals.badge_text};
`;

const ActionText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme }) => theme.signals.text_secondary};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: ${({ theme }) => theme.signals.text_muted};
  font-weight: 600;
  margin-top: auto;
`;

const FooterDot = styled.span<{ $status: SignalStatus }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.signals.text_muted};
  display: inline-block;
`;

const FooterButtonGroup = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const FooterIconButton = styled.button<{ $status?: SignalStatus }>`
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme, $status }) =>
    $status === 'red' || $status === 'amber'
      ? tokenForStatus(theme, $status).border
      : theme.signals.card_border};
  border-radius: 999px;
  background: ${({ theme, $status }) =>
    $status === 'red' || $status === 'amber'
      ? tokenForStatus(theme, $status).tint
      : theme.signals.chip_bg};
  color: ${({ theme, $status }) =>
    $status === 'red' || $status === 'amber'
      ? tokenForStatus(theme, $status).text
      : theme.signals.text_secondary};
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  box-shadow: ${({ theme, $status }) =>
    $status === 'red' || $status === 'amber'
      ? `0 0 0 3px ${tokenForStatus(theme, $status).tint}`
      : 'none'};

  &:hover {
    color: ${({ theme, $status }) =>
      $status === 'red' || $status === 'amber'
        ? tokenForStatus(theme, $status).strong
        : theme.signals.text_primary};
  }
`;

const ConfigureButton = styled(FooterIconButton)`
  svg {
    display: block;
  }
`;

const BackHeader = styled(Header)`
  align-items: flex-start;
`;

const BackScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
  min-height: 0;
  padding-right: 2px;
`;

const BackSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  background: ${({ theme }) => theme.signals.chip_bg};
  border: 1px solid ${({ theme }) => theme.signals.divider};
`;

const SectionTitle = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.text_muted};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const ToggleTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.signals.text_primary};
`;

const ToggleHint = styled.div`
  font-size: 10px;
  line-height: 1.35;
  color: ${({ theme }) => theme.signals.text_muted};
`;

const SwitchButton = styled.button<{ $on: boolean }>`
  width: 42px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  padding: 3px;
  background: ${({ theme, $on }) =>
    $on ? theme.signals.green_strong : theme.signals.neutral_strong};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;

  span {
    display: block;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => theme.signals.card_bg};
    transform: translateX(${({ $on }) => ($on ? '18px' : '0')});
    transition: transform 0.2s ease;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.signals.card_border};
  border-radius: 999px;
  background: ${({ theme }) => theme.signals.card_bg};
  color: ${({ theme }) => theme.signals.text_secondary};
  font-size: 11px;
  font-weight: 800;
  padding: 7px 10px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.signals.text_primary};
  }
`;

const DangerButton = styled(SecondaryButton)`
  color: ${({ theme }) => theme.signals.red_text};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const TunableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TuningLoading = styled.div`
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.signals.divider};
  border-radius: 10px;
`;

const TunableRow = styled.label<{ $severity?: 'red' | 'amber' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;

  input {
    width: 100%;
    accent-color: ${({ theme, $severity }) =>
      $severity ? tokenForStatus(theme, $severity).strong : theme.signals.green_strong};
  }

  input::-webkit-slider-thumb {
    background: ${({ theme, $severity }) =>
      $severity ? tokenForStatus(theme, $severity).strong : theme.signals.green_strong};
  }

  input::-moz-range-thumb {
    background: ${({ theme, $severity }) =>
      $severity ? tokenForStatus(theme, $severity).strong : theme.signals.green_strong};
  }
`;

const TunableLabel = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 11px;
  color: ${({ theme }) => theme.signals.text_secondary};

  strong {
    color: ${({ theme }) => theme.signals.text_primary};
  }
`;

const BackCopy = styled.p`
  margin: 0;
  font-size: 11px;
  line-height: 1.45;
  color: ${({ theme }) => theme.signals.text_secondary};
`;

const BackFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.signals.divider};
`;
